import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { DollarSign, IndianRupee, Settings, Save, RefreshCw } from "lucide-react";
import HomePricingPlansManager from "./HomePricingPlansManager";

interface CurrencySettings {
  premium_price_inr: string;
  premium_price_usd: string;
  currency_enabled_inr: boolean;
  currency_enabled_usd: boolean;
  razorpay_international_status: string;
}

const CurrencySettingsManager = () => {
  const [settings, setSettings] = useState<CurrencySettings>({
    premium_price_inr: '149',
    premium_price_usd: '8',
    currency_enabled_inr: true,
    currency_enabled_usd: false,
    razorpay_international_status: 'pending'
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('printables_settings')
        .select('setting_key, setting_value')
        .in('setting_key', [
          'premium_price_inr',
          'premium_price_usd', 
          'currency_enabled_inr',
          'currency_enabled_usd',
          'razorpay_international_status'
        ]);

      if (error) throw error;

      if (data) {
        const settingsObj: any = {};
        data.forEach(item => {
          if (item.setting_key.includes('enabled')) {
            settingsObj[item.setting_key] = item.setting_value === 'true';
          } else {
            settingsObj[item.setting_key] = item.setting_value;
          }
        });
        setSettings(prev => ({ ...prev, ...settingsObj }));
      }
    } catch (error) {
      console.error('Error fetching currency settings:', error);
      toast({
        title: "Error",
        description: "Failed to fetch currency settings",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    try {
      setSaving(true);
      
      const updates = [
        { setting_key: 'premium_price_inr', setting_value: settings.premium_price_inr },
        { setting_key: 'premium_price_usd', setting_value: settings.premium_price_usd },
        { setting_key: 'currency_enabled_inr', setting_value: settings.currency_enabled_inr.toString() },
        { setting_key: 'currency_enabled_usd', setting_value: settings.currency_enabled_usd.toString() },
        { setting_key: 'razorpay_international_status', setting_value: settings.razorpay_international_status },
      ];

      for (const update of updates) {
        const { error } = await supabase
          .from('printables_settings')
          .upsert(update, { onConflict: 'setting_key' });
        
        if (error) throw error;
      }

      toast({
        title: "Settings Saved",
        description: "Currency settings have been updated successfully",
      });
    } catch (error) {
      console.error('Error saving currency settings:', error);
      toast({
        title: "Error",
        description: "Failed to save currency settings",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleInputChange = (key: keyof CurrencySettings, value: string | boolean) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  const enableUSDPayments = () => {
    setSettings(prev => ({
      ...prev,
      currency_enabled_usd: true,
      razorpay_international_status: 'approved'
    }));
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Settings className="w-5 h-5" />
            Currency & Payment Settings
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <RefreshCw className="w-6 h-6 animate-spin" />
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Settings className="w-5 h-5" />
          Currency & Payment Settings
        </CardTitle>
        <p className="text-sm text-gray-600">
          Manage pricing and payment availability for different currencies
        </p>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* INR Settings */}
        <div className="space-y-4 p-4 border rounded-lg">
          <div className="flex items-center gap-2">
            <IndianRupee className="w-5 h-5" />
            <h3 className="text-lg font-semibold">Indian Rupees (INR)</h3>
            <Badge variant={settings.currency_enabled_inr ? "default" : "secondary"}>
              {settings.currency_enabled_inr ? "Enabled" : "Disabled"}
            </Badge>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="inr-price">Premium Price (INR)</Label>
              <Input
                id="inr-price"
                type="number"
                value={settings.premium_price_inr}
                onChange={(e) => handleInputChange('premium_price_inr', e.target.value)}
                placeholder="149"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="inr-enabled">Enable INR Payments</Label>
              <div className="flex items-center space-x-2 pt-2">
                <Switch
                  id="inr-enabled"
                  checked={settings.currency_enabled_inr}
                  onCheckedChange={(checked) => handleInputChange('currency_enabled_inr', checked)}
                />
                <span className="text-sm text-gray-600">
                  {settings.currency_enabled_inr ? "Enabled" : "Disabled"}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* USD Settings */}
        <div className="space-y-4 p-4 border rounded-lg">
          <div className="flex items-center gap-2">
            <DollarSign className="w-5 h-5" />
            <h3 className="text-lg font-semibold">US Dollars (USD)</h3>
            <Badge variant={settings.currency_enabled_usd ? "default" : "secondary"}>
              {settings.currency_enabled_usd ? "Enabled" : "Disabled"}
            </Badge>
            <Badge variant={settings.razorpay_international_status === 'approved' ? "default" : "destructive"}>
              Razorpay International: {settings.razorpay_international_status}
            </Badge>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="usd-price">Premium Price (USD)</Label>
              <Input
                id="usd-price"
                type="number"
                value={settings.premium_price_usd}
                onChange={(e) => handleInputChange('premium_price_usd', e.target.value)}
                placeholder="8"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="usd-enabled">Enable USD Payments</Label>
              <div className="flex items-center space-x-2 pt-2">
                <Switch
                  id="usd-enabled"
                  checked={settings.currency_enabled_usd}
                  onCheckedChange={(checked) => handleInputChange('currency_enabled_usd', checked)}
                />
                <span className="text-sm text-gray-600">
                  {settings.currency_enabled_usd ? "Enabled" : "Disabled"}
                </span>
              </div>
            </div>
          </div>

          {settings.razorpay_international_status === 'pending' && (
            <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
              <p className="text-sm text-yellow-800">
                <strong>Note:</strong> USD payments require Razorpay International approval. 
                Once approved, click the button below to enable USD payments.
              </p>
              <Button
                onClick={enableUSDPayments}
                className="mt-2"
                variant="outline"
                size="sm"
              >
                Enable USD Payments (Once Approved)
              </Button>
            </div>
          )}
        </div>

        {/* Razorpay Status */}
        <div className="space-y-4 p-4 border rounded-lg">
          <h3 className="text-lg font-semibold">Razorpay International Status</h3>
          <div className="space-y-2">
            <Label htmlFor="razorpay-status">Status</Label>
            <select
              id="razorpay-status"
              value={settings.razorpay_international_status}
              onChange={(e) => handleInputChange('razorpay_international_status', e.target.value)}
              className="w-full p-2 border rounded-md"
            >
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>
        </div>

        {/* Save Button */}
        <div className="flex justify-end space-x-2">
          <Button onClick={fetchSettings} variant="outline">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          <Button onClick={saveSettings} disabled={saving}>
            {saving ? (
              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Save className="w-4 h-4 mr-2" />
            )}
            Save Settings
          </Button>
        </div>
      </CardContent>
    </Card>

    <HomePricingPlansManager />
    </div>
  );
};

export default CurrencySettingsManager;